import { QueryClientProvider } from "@tanstack/react-query";
import type { ReactElement, ReactNode } from "react";

import { ActiveOrganizationProvider } from "../shared/api/activeOrganization";
import { queryClient } from "../shared/api/queryClient";
import { SessionProvider } from "../shared/api/session";
import { ThemeProvider } from "../shared/theme/ThemeProvider";
import { ToastProvider } from "../shared/ui/molecules/ToastProvider";

export interface AppProvidersProps {
  readonly children: ReactNode;
}

/**
 * The composition root, with the five providers of design section 4.
 *
 * The order is not decoration:
 *
 *   - `ThemeProvider` is outermost because it depends on nothing, and a
 *     failure anywhere below it must still be readable in the right theme.
 *   - `QueryClientProvider` comes next because the session IS a query (DD9),
 *     so `SessionProvider` cannot exist above it.
 *   - `ToastProvider` comes before the session because the 401 interceptor and
 *     the sign-out path both raise notices.
 *   - `SessionProvider` runs the one bootstrap request.
 *   - `ActiveOrganizationProvider` sits INSIDE it, because the active
 *     organization is derived from its scopes and does not exist without them
 *     (DD9b).
 *
 * `RouterProvider` is the sixth and last of the order, and it is mounted by
 * whoever renders the application inside these five: `src/main.tsx` in the
 * browser, and a memory router in the tests that need one.
 */
export function AppProviders({ children }: AppProvidersProps): ReactElement {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <SessionProvider>
            <ActiveOrganizationProvider>{children}</ActiveOrganizationProvider>
          </SessionProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
